/**
 * Боковая панель легенды: открывается по клику метки и показывает развёрнутое
 * rich-text описание соответствующего элемента легенды (Требование 5.2, 6.5).
 *
 * Хранимое `richDescription` элемента легенды — это HTML, санитизированный на
 * сервере при сохранении. Тем не менее панель **повторно санитизирует** контент
 * перед вставкой в DOM (защита в глубину от XSS): даже если в БД по какой-то
 * причине попал небезопасный HTML, он не будет исполнен. Санитайзер
 * ({@link sanitizeRichHtml}) — чистая строковая функция без DOM, поэтому
 * покрывается юнит-тестами в не-браузерном окружении.
 *
 * DOM-часть ({@link LegendPanel}) создаёт всплывающую панель сбоку экрана с
 * иконкой, названием, кратким описанием и санитизированным rich-text блоком.
 * Панель закрывается кнопкой-крестиком, клавишей Escape или кликом вне панели.
 * В не-браузерном окружении (тесты shared-логики) конструктор и методы работы с
 * DOM безопасно вырождаются в no-op.
 *
 * Требования: 5.2, 6.5
 */

import type { LegendItem } from '@langton/shared';

/** CSS-класс корневого элемента панели. */
const PANEL_CLASS = 'legend-panel';
/** Модификатор стороны появления панели. */
const PANEL_SIDE_CLASS_PREFIX = 'legend-panel--';
/** Модификатор открытого состояния. */
const PANEL_OPEN_CLASS = 'legend-panel--open';

// ---------------------------------------------------------------------------
// Санитайзер rich-text (чистая функция, без DOM)
// ---------------------------------------------------------------------------

/**
 * Разрешённые теги и допустимые для каждого из них атрибуты. Теги, отсутствующие
 * в списке, удаляются (их текстовое содержимое сохраняется). Атрибуты, не
 * входящие в список для тега, отбрасываются.
 */
const ALLOWED_TAGS: Readonly<Record<string, readonly string[]>> = {
  h1: [],
  h2: [],
  h3: [],
  h4: [],
  h5: [],
  h6: [],
  p: [],
  br: [],
  hr: [],
  strong: [],
  em: [],
  b: [],
  i: [],
  u: [],
  s: [],
  ul: [],
  ol: [],
  li: [],
  a: ['href', 'title', 'target', 'rel'],
  img: ['src', 'alt', 'title', 'width', 'height'],
  blockquote: [],
  code: [],
  pre: [],
  span: [],
};

/** Теги без закрывающего тега (void-элементы). */
const VOID_TAGS = new Set(['br', 'hr', 'img']);

/**
 * Теги, удаляемые вместе с содержимым (потенциально исполняемый контент).
 * Их внутренний текст не должен попадать в вывод.
 */
const CONTENT_STRIP_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'noscript'];

/** Атрибуты, значения которых являются URL и требуют проверки протокола. */
const URL_ATTRS = new Set(['href', 'src']);

/** Безопасные протоколы для обычных ссылок. */
const SAFE_PROTOCOLS = new Set(['http', 'https', 'mailto', 'tel']);

/**
 * Декодировать наиболее частые HTML-сущности в значении атрибута, чтобы проверка
 * протокола не обходилась кодированием (например, `java&#115;cript:`).
 */
function decodeEntities(value: string): string {
  return value
    .replace(/&#x([0-9a-fA-F]+);?/g, (_, hex: string) =>
      String.fromCodePoint(parseInt(hex, 16)),
    )
    .replace(/&#(\d+);?/g, (_, dec: string) =>
      String.fromCodePoint(parseInt(dec, 10)),
    )
    .replace(/&amp;/gi, '&')
    .replace(/&colon;/gi, ':')
    .replace(/&tab;/gi, '\t')
    .replace(/&newline;/gi, '\n');
}

/**
 * Проверить, безопасен ли URL для атрибутов `href`/`src`.
 * Разрешены относительные ссылки, якоря и протоколы http/https/mailto/tel, а для
 * `data:` — только изображения (`data:image/...`). Отклоняются `javascript:`,
 * `vbscript:`, `data:text/html` и прочие небезопасные схемы.
 */
function isSafeUrl(rawUrl: string): boolean {
  const decoded = decodeEntities(rawUrl);
  // Убрать управляющие символы и пробелы, которые браузер игнорирует в URL
  // (фильтр по коду символа, чтобы не использовать управляющие коды в регексе).
  let cleaned = '';
  for (const ch of decoded) {
    const code = ch.charCodeAt(0);
    if (code > 0x20 && code !== 0x7f) {
      cleaned += ch;
    }
  }
  cleaned = cleaned.toLowerCase();
  if (cleaned === '') {
    return false;
  }
  if (cleaned.startsWith('data:')) {
    return cleaned.startsWith('data:image/');
  }
  const protoMatch = /^([a-z][a-z0-9+.-]*):/.exec(cleaned);
  if (protoMatch) {
    return SAFE_PROTOCOLS.has(protoMatch[1]);
  }
  // Нет схемы — относительный URL, якорь `#...` или путь: безопасно.
  return true;
}

/** Экранировать значение атрибута для безопасной вставки в двойные кавычки. */
function escapeAttrValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Разобранный атрибут тега. */
interface ParsedAttr {
  name: string;
  value: string;
}

/** Регекс разбора отдельного атрибута `name` или `name="value"`. */
const ATTR_RE =
  /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

/** Разобрать строку атрибутов открывающего тега в пары имя/значение. */
function parseAttributes(raw: string): ParsedAttr[] {
  const attrs: ParsedAttr[] = [];
  let match: RegExpExecArray | null;
  ATTR_RE.lastIndex = 0;
  while ((match = ATTR_RE.exec(raw)) !== null) {
    const name = match[1];
    const value = match[2] ?? match[3] ?? match[4] ?? '';
    attrs.push({ name, value });
  }
  return attrs;
}

/**
 * Собрать безопасную строку атрибутов для тега по его allowlist.
 * Отбрасываются: обработчики событий (`on*`), атрибуты вне списка, `style`,
 * а также `href`/`src` с небезопасным URL.
 */
function sanitizeAttributes(tag: string, raw: string): string {
  const allowed = ALLOWED_TAGS[tag];
  if (!allowed || allowed.length === 0) {
    return '';
  }
  const parts: string[] = [];
  let hasTargetBlank = false;
  for (const { name, value } of parseAttributes(raw)) {
    const lower = name.toLowerCase();
    if (lower.startsWith('on')) {
      continue; // обработчики событий недопустимы
    }
    if (!allowed.includes(lower)) {
      continue; // атрибут не разрешён для этого тега
    }
    if (URL_ATTRS.has(lower) && !isSafeUrl(value)) {
      continue; // небезопасный URL
    }
    if (lower === 'target' && value.toLowerCase() === '_blank') {
      hasTargetBlank = true;
    }
    parts.push(`${lower}="${escapeAttrValue(value)}"`);
  }
  // Для ссылок, открывающихся в новой вкладке, добавить защиту от tabnabbing.
  if (tag === 'a' && hasTargetBlank && !/\brel=/i.test(raw)) {
    parts.push('rel="noopener noreferrer"');
  }
  return parts.length > 0 ? ' ' + parts.join(' ') : '';
}

/** Регекс тега: открывающий/закрывающий, с учётом кавычек в значениях атрибутов. */
const TAG_RE =
  /<(\/)?([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[^\s"'=<>`/]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*(\/)?>/g;

/**
 * Санитизировать rich-text HTML по строгому allowlist тегов и атрибутов.
 *
 * Поведение:
 * - HTML-комментарии удаляются.
 * - Теги `script`/`style`/`iframe`/`object`/`embed`/`noscript` удаляются вместе
 *   с содержимым.
 * - Разрешённые теги сохраняются (заголовки, абзацы, форматирование, списки,
 *   ссылки, изображения, цитаты, код). Прочие теги удаляются, но их текст
 *   остаётся.
 * - Обработчики событий (`on*`), `style` и атрибуты вне списка отбрасываются.
 * - `href`/`src` с `javascript:`, `vbscript:`, `data:text/html` и подобными
 *   схемами удаляются; сохраняются http/https/mailto/tel, относительные ссылки и
 *   `data:image/...`.
 *
 * Чистая функция без DOM — вызывается перед вставкой контента в панель как
 * защита в глубину и покрывается юнит-тестами.
 *
 * @param html исходный (возможно, уже санитизированный на сервере) HTML
 * @returns безопасный HTML для вставки через `innerHTML`
 */
export function sanitizeRichHtml(html: string): string {
  if (typeof html !== 'string' || html === '') {
    return '';
  }

  // 1. Удалить комментарии.
  let input = html.replace(/<!--[\s\S]*?-->/g, '');

  // 2. Удалить опасные элементы вместе с содержимым (в т.ч. незакрытые).
  for (const tag of CONTENT_STRIP_TAGS) {
    const paired = new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}\\s*>`, 'gi');
    input = input.replace(paired, '');
    const dangling = new RegExp(`<\\/?${tag}\\b[^>]*>`, 'gi');
    input = input.replace(dangling, '');
  }

  // 3. Пройтись по тегам, сохраняя текст между ними без изменений.
  let output = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  TAG_RE.lastIndex = 0;
  while ((match = TAG_RE.exec(input)) !== null) {
    output += input.slice(lastIndex, match.index);
    lastIndex = TAG_RE.lastIndex;

    const isClosing = Boolean(match[1]);
    const tag = match[2].toLowerCase();
    const rawAttrs = match[3] ?? '';

    if (!Object.prototype.hasOwnProperty.call(ALLOWED_TAGS, tag)) {
      continue; // недопустимый тег — отбрасываем, текст уже сохранён
    }

    if (isClosing) {
      if (!VOID_TAGS.has(tag)) {
        output += `</${tag}>`;
      }
      continue;
    }

    const attrs = sanitizeAttributes(tag, rawAttrs);
    if (VOID_TAGS.has(tag)) {
      output += `<${tag}${attrs} />`;
    } else {
      output += `<${tag}${attrs}>`;
    }
  }
  output += input.slice(lastIndex);

  return output;
}

// ---------------------------------------------------------------------------
// Боковая панель (DOM)
// ---------------------------------------------------------------------------

/** Сторона появления панели. */
export type LegendPanelSide = 'left' | 'right';

/** Опции конфигурации {@link LegendPanel}. */
export interface LegendPanelOptions {
  /**
   * Контейнер, в который добавляется панель. По умолчанию — `document.body`.
   * Панель позиционируется абсолютно/фиксированно относительно окна.
   */
  container?: HTMLElement;
  /** Сторона появления панели (по умолчанию `'right'`). */
  side?: LegendPanelSide;
  /** Колбэк, вызываемый при закрытии панели (крестик/Escape/клик вне). */
  onClose?: () => void;
}

/**
 * Боковая панель rich-text просмотра элемента легенды.
 *
 * Использование:
 * 1. `const panel = new LegendPanel({ side: 'right' })`.
 * 2. По клику метки: `panel.open(legendItem)` — панель показывает иконку,
 *    название, краткое описание и санитизированный HTML развёрнутого описания.
 * 3. `panel.close()` / крестик / Escape / клик вне панели — скрыть.
 * 4. `panel.dispose()` — снять обработчики и удалить DOM.
 */
export class LegendPanel {
  private readonly options: Required<Omit<LegendPanelOptions, 'onClose'>> & {
    onClose?: () => void;
  };

  private panel: HTMLElement | null = null;
  private iconEl: HTMLImageElement | null = null;
  private titleEl: HTMLElement | null = null;
  private shortDescEl: HTMLElement | null = null;
  private richEl: HTMLElement | null = null;

  private open_ = false;

  private readonly keydownHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.close();
    }
  };
  private readonly outsideHandler = (e: MouseEvent): void => {
    if (this.panel && e.target instanceof Node && !this.panel.contains(e.target)) {
      this.close();
    }
  };

  /**
   * @param options контейнер, сторона появления и колбэк закрытия
   */
  constructor(options: LegendPanelOptions = {}) {
    this.options = {
      container:
        options.container ??
        (typeof document !== 'undefined'
          ? document.body
          : (null as unknown as HTMLElement)),
      side: options.side ?? 'right',
      onClose: options.onClose,
    };
    if (typeof document !== 'undefined') {
      this.build();
    }
  }

  /** Открыта ли панель в данный момент. */
  isOpen(): boolean {
    return this.open_;
  }

  /**
   * Открыть панель и показать содержимое элемента легенды: иконку (если есть),
   * название, краткое описание и санитизированный rich-text (Требование 5.2, 6.5).
   *
   * @param item элемент легенды для отображения
   */
  open(item: LegendItem): void {
    if (!this.panel || typeof document === 'undefined') {
      return;
    }
    this.render(item);
    this.panel.classList.add(PANEL_OPEN_CLASS);
    this.panel.style.display = 'block';
    this.panel.setAttribute('aria-hidden', 'false');
    this.open_ = true;

    // Обработчики Escape и клика вне панели. Клик вне вешаем асинхронно, чтобы
    // тот же клик, что открыл панель (по метке), её сразу не закрыл.
    document.addEventListener('keydown', this.keydownHandler);
    setTimeout(() => {
      if (this.open_) {
        document.addEventListener('mousedown', this.outsideHandler);
      }
    }, 0);
  }

  /** Закрыть панель и снять временные обработчики. */
  close(): void {
    if (!this.open_) {
      return;
    }
    this.open_ = false;
    if (this.panel) {
      this.panel.classList.remove(PANEL_OPEN_CLASS);
      this.panel.style.display = 'none';
      this.panel.setAttribute('aria-hidden', 'true');
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.keydownHandler);
      document.removeEventListener('mousedown', this.outsideHandler);
    }
    this.options.onClose?.();
  }

  /** Снять все обработчики и удалить панель из DOM. */
  dispose(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.keydownHandler);
      document.removeEventListener('mousedown', this.outsideHandler);
    }
    this.panel?.remove();
    this.panel = null;
    this.iconEl = null;
    this.titleEl = null;
    this.shortDescEl = null;
    this.richEl = null;
    this.open_ = false;
  }

  /** Корневой DOM-элемент панели (для тестов/интеграции), либо `null`. */
  get element(): HTMLElement | null {
    return this.panel;
  }

  /** Построить структуру панели (заголовок, крестик, иконка, тексты). */
  private build(): void {
    const panel = document.createElement('aside');
    panel.className = `${PANEL_CLASS} ${PANEL_SIDE_CLASS_PREFIX}${this.options.side}`;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-hidden', 'true');
    panel.style.display = 'none';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'legend-panel__close';
    closeButton.setAttribute('aria-label', 'Закрыть');
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => this.close());

    const header = document.createElement('div');
    header.className = 'legend-panel__header';

    const icon = document.createElement('img');
    icon.className = 'legend-panel__icon';
    icon.alt = '';
    icon.style.display = 'none';

    const title = document.createElement('h2');
    title.className = 'legend-panel__title';

    header.appendChild(icon);
    header.appendChild(title);

    const shortDesc = document.createElement('p');
    shortDesc.className = 'legend-panel__short';
    shortDesc.style.display = 'none';

    const rich = document.createElement('div');
    rich.className = 'legend-panel__rich';

    panel.appendChild(closeButton);
    panel.appendChild(header);
    panel.appendChild(shortDesc);
    panel.appendChild(rich);

    if (this.options.container) {
      this.options.container.appendChild(panel);
    }

    this.panel = panel;
    this.iconEl = icon;
    this.titleEl = title;
    this.shortDescEl = shortDesc;
    this.richEl = rich;
  }

  /** Заполнить панель содержимым элемента легенды. */
  private render(item: LegendItem): void {
    if (this.iconEl) {
      if (item.iconUrl && isSafeUrl(item.iconUrl)) {
        this.iconEl.src = item.iconUrl;
        this.iconEl.alt = item.name ?? '';
        this.iconEl.style.display = '';
      } else {
        this.iconEl.removeAttribute('src');
        this.iconEl.style.display = 'none';
      }
    }
    if (this.titleEl) {
      // textContent — без интерпретации HTML (название не rich-text).
      this.titleEl.textContent = item.name ?? '';
    }
    if (this.shortDescEl) {
      if (item.shortDescription) {
        this.shortDescEl.textContent = item.shortDescription;
        this.shortDescEl.style.display = '';
      } else {
        this.shortDescEl.textContent = '';
        this.shortDescEl.style.display = 'none';
      }
    }
    if (this.richEl) {
      // Повторная санитизация перед вставкой (защита в глубину, Требование 6.5).
      this.richEl.innerHTML = sanitizeRichHtml(item.richDescription ?? '');
    }
  }
}
